// Goal Timing & Quarter Analysis (v3 teardown).
//
// Lives at /goal-timing. Daily slate → fixture analyser (quarters +
// survival + HT/SH markets) → performance tracker, all under the v3
// Quiet Precision chrome.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Clock } from "lucide-react";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import TopNav from "../components/TopNav";
import DailyValueSlateCard from "../components/DailyValueSlateCard";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Section, V3Label,
  V3Tag, V3Num, V3Segment, V3Button,
} from "../components/v3";

const TIER_TONES = {
  "A+": "emerald", "A": "emerald",
  "B+": "amber",   "B": "amber",
  "C": "mute",
  "NO BET": "rose",
};

const STYLE_OPTIONS = [
  { value: "balanced", label: "Balanced" },
  { value: "fast_starter", label: "Fast Starter" },
  { value: "slow_starter", label: "Slow Starter" },
  { value: "first_half_dominant", label: "1H Dominant" },
  { value: "second_half_dominant", label: "2H Dominant" },
  { value: "late_scorer", label: "Late Scorer" },
  { value: "defensive_opener", label: "Defensive Opener" },
  { value: "aggressive_press", label: "Aggressive Press" },
  { value: "counterattacking", label: "Counterattacking" },
];

const SURVIVAL_LABELS = {
  goal_before_15: "Goal before 15'",
  goal_before_30: "Goal before 30'",
  goal_before_ht: "Goal before HT",
  goal_after_75: "Goal after 75'",
  no_goal_first_30: "No goal first 30'",
  no_goal_first_half: "No goal first half",
};


export default function GoalTimingPage() {
  return (
    <V3Page testid="gt-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="gt-header"
          eyebrow={
            <V3Eyebrow icon={Clock} backTo="/" backLabel="Home">
              Hazard · Quarters · HT &amp; SH · 50k MC
            </V3Eyebrow>
          }
          title="Goal timing &amp; quarters."
          titleAccent="When goals land, not just how many."
          subtitle="Survival / hazard model across six 15-minute quarters with first-half / second-half markets, late-goal probabilities and team timing styles."
        />

        <V3Section label="Daily slate" testid="gt-slate-section">
          <DailyValueSlateCard variant="goal_timing" testidPrefix="gt-slate" />
        </V3Section>

        <AnalyseSection />
        <PerformanceSection />
      </V3Container>
    </V3Page>
  );
}


function AnalyseSection() {
  const [form, setForm] = useState({
    home_team: "Brentford",
    away_team: "Liverpool",
    league: "Premier League",
    lambda_home: "1.2",
    lambda_away: "2.1",
    home_style: "balanced",
    away_style: "fast_starter",
  });
  const [marketLines, setMarketLines] = useState([
    { market: "Over 1.5 HT",    selection: "Over", odds: "" },
    { market: "HT BTTS",        selection: "Yes",  odds: "" },
    { market: "Late goal (76+)", selection: "Yes", odds: "" },
  ]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setLine  = (i, k, v) => setMarketLines((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  async function run() {
    setLoading(true);
    setError(null);
    try {
      const market_lines = Object.fromEntries(
        marketLines
          .filter((r) => r.odds)
          .map((r) => [`${r.market}|${r.selection}`, { odds: parseFloat(r.odds) }]),
      );
      const res = await axios.post(`${API}/goal-timing/analyze`, {
        home_team: form.home_team, away_team: form.away_team, league: form.league,
        lambda_home: parseFloat(form.lambda_home),
        lambda_away: parseFloat(form.lambda_away),
        home_style: form.home_style, away_style: form.away_style,
        market_lines, mc_iterations: 5000,
      });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Analyse failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <V3Section label="Analyse fixture" testid="gt-analyse-section">
      <p className="text-[13px] mb-6 max-w-2xl" style={{ color: "var(--v3-text-3)" }}>
        Provide expected goal rates + a team-style hint per side. We'll
        compute quarter probabilities, HT &amp; SH market fair odds, survival
        probabilities, and run 5 000 Monte Carlo paths.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Home team" testId="gt-home-team" value={form.home_team}
               onChange={(v) => setField("home_team", v)} />
        <Field label="Away team" testId="gt-away-team" value={form.away_team}
               onChange={(v) => setField("away_team", v)} />
        <Field label="League"    testId="gt-league"    value={form.league}
               onChange={(v) => setField("league", v)} />
        <Field label="λ home" testId="gt-lambda-home" type="number"
               value={form.lambda_home} onChange={(v) => setField("lambda_home", v)} />
        <Field label="λ away" testId="gt-lambda-away" type="number"
               value={form.lambda_away} onChange={(v) => setField("lambda_away", v)} />
        <div />
        <Select label="Home style" testId="gt-home-style" value={form.home_style}
                onChange={(v) => setField("home_style", v)} options={STYLE_OPTIONS} />
        <Select label="Away style" testId="gt-away-style" value={form.away_style}
                onChange={(v) => setField("away_style", v)} options={STYLE_OPTIONS} />
      </div>

      <div className="mt-8">
        <V3Label>Bookmaker odds (optional)</V3Label>
        <div className="mt-3" style={{ borderTop: "1px solid var(--v3-line)" }}>
          {marketLines.map((r, i) => (
            <div
              key={i}
              className="flex gap-2 items-center py-2 flex-wrap"
              style={{ borderBottom: "1px solid var(--v3-line)" }}
              data-testid={`gt-market-row-${i}`}
            >
              <MonoInput value={r.market}    onChange={(v) => setLine(i, "market", v)}    width={180} testId={`gt-market-${i}`} />
              <MonoInput value={r.selection} onChange={(v) => setLine(i, "selection", v)} width={112} testId={`gt-selection-${i}`} />
              <MonoInput value={r.odds}      onChange={(v) => setLine(i, "odds", v)}      width={96}  testId={`gt-odds-${i}`} type="number" placeholder="odds" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <V3Button onClick={run} disabled={loading} data-testid="gt-run-analysis">
          {loading ? "Analysing…" : "Run analysis"}
        </V3Button>
        {error && (
          <span
            className="text-[12px]"
            style={{ color: "var(--v3-rose, #fda4af)", fontFamily: "var(--v3-font-mono)" }}
            data-testid="gt-error"
          >
            {error}
          </span>
        )}
      </div>

      {result && <AnalysisResult data={result} />}
    </V3Section>
  );
}


function AnalysisResult({ data }) {
  const tierTone = TIER_TONES[data.risk_tier] || "mute";
  const quarters = useMemo(() =>
    Object.entries(data.quarter_probabilities), [data.quarter_probabilities]);

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-0" style={{ borderTop: "1px solid var(--v3-line)" }} data-testid="gt-analysis-result">
      <Pane borderRight borderBottom>
        <V3Label>Verdict</V3Label>
        <div className="mt-3 flex items-center gap-3">
          <V3Tag tone={tierTone} testid="gt-verdict-tier">{data.risk_tier}</V3Tag>
          <V3Num className="text-xl">conf {data.confidence_pct}%</V3Num>
        </div>
        {data.best_timing_market ? (
          <>
            <div className="mt-4 text-[14px]" style={{ fontWeight: 500 }}>
              {data.best_timing_market.market} · {data.best_timing_market.selection}
            </div>
            <div
              className="mt-2 text-3xl"
              data-testid="gt-edge"
              style={{ color: "var(--v3-indigo-fg)", fontFamily: "var(--v3-font-mono)", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}
            >
              +{data.best_timing_market.edge_pct.toFixed(2)}%
            </div>
            <div className="mt-3 text-[11px]" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}>
              Market {data.best_timing_market.market_odds} · Fair {data.best_timing_market.fair_odds}
              <br />
              Fair prob {(data.best_timing_market.fair_probability * 100).toFixed(1)}%
            </div>
          </>
        ) : (
          <div className="mt-4 text-[13px]" style={{ color: "var(--v3-text-3)" }} data-testid="gt-no-bet">
            {data.no_bet_reason || "Add bookmaker odds to score edges."}
          </div>
        )}
      </Pane>

      <Pane borderRight borderBottom>
        <V3Label>Half splits</V3Label>
        <div className="mt-3 grid grid-cols-2 gap-6 text-center">
          <div>
            <V3Num className="text-2xl">{data.first_half_expected_goals}</V3Num>
            <div className="text-[11px] mt-1" style={{ color: "var(--v3-text-3)" }}>1H expected goals</div>
          </div>
          <div>
            <V3Num className="text-2xl">{data.second_half_expected_goals}</V3Num>
            <div className="text-[11px] mt-1" style={{ color: "var(--v3-text-3)" }}>2H expected goals</div>
          </div>
        </div>
        <KVList
          rows={[
            ["Highest scoring half", <span className="capitalize" style={{ fontWeight: 500 }}>{data.highest_scoring_half}</span>],
            ["Most dangerous",       <span style={{ fontWeight: 500 }} data-testid="gt-most-dangerous">{data.quarter_ranking[0]}</span>],
            ["2nd most dangerous",   <span style={{ fontWeight: 500 }}>{data.quarter_ranking[1]}</span>],
            ["Home style",           <span style={{ fontWeight: 500 }}>{data.home_style}</span>],
            ["Away style",           <span style={{ fontWeight: 500 }}>{data.away_style}</span>],
          ]}
        />
      </Pane>

      <Pane borderBottom>
        <V3Label>Quarter probabilities</V3Label>
        <div className="mt-3 space-y-2">
          {quarters.map(([q, pct]) => (
            <Bar key={q} label={q} pct={pct} tone={q === data.quarter_ranking[0] ? "var(--v3-rose, #f43f5e)" : "var(--v3-indigo)"} />
          ))}
        </div>
      </Pane>

      <div
        className="lg:col-span-3 p-5"
        style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)", borderTop: "none" }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <V3Label>Survival probabilities</V3Label>
            <div className="mt-3 space-y-2">
              {Object.entries(data.survival_probabilities).map(([k, pct]) => (
                <Bar key={k} label={SURVIVAL_LABELS[k] || k} pct={pct} wide />
              ))}
            </div>
          </div>
          <MarketsTable title="HT markets" rows={data.ht_markets} />
        </div>
        <div className="mt-6">
          <MarketsTable title="SH markets" rows={data.sh_markets} />
        </div>
      </div>
    </div>
  );
}


function MarketsTable({ title, rows }) {
  return (
    <div>
      <V3Label>{title}</V3Label>
      <table className="w-full text-[13px] mt-3" style={{ fontVariantNumeric: "tabular-nums" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--v3-line)" }}>
            {["Market", "Selection", "Fair prob", "Fair odds", "Market", "Edge"].map((h) => (
              <th key={h} className="py-2 text-left text-[10px] uppercase pr-3" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((e, i) => (
            <tr
              key={`${e.market}-${e.selection}-${i}`}
              style={{ borderBottom: "1px solid var(--v3-line)" }}
              data-testid={`gt-row-${title.replace(/\s/g, '-')}-${i}`}
            >
              <td className="py-1.5 pr-3">{e.market}</td>
              <td className="pr-3" style={{ color: "var(--v3-text-2)" }}>{e.selection}</td>
              <td className="pr-3">{(e.fair_probability * 100).toFixed(1)}%</td>
              <td className="pr-3">{e.fair_odds}</td>
              <td className="pr-3">{e.market_odds ?? "—"}</td>
              <td
                className="pr-3"
                style={{
                  color:
                    e.edge_pct == null ? "var(--v3-text-4)" :
                    e.edge_pct >= 3   ? "var(--v3-indigo-fg)" :
                    e.edge_pct >= 0   ? "var(--v3-text)" : "var(--v3-rose, #fda4af)",
                  fontWeight: e.edge_pct != null && e.edge_pct >= 3 ? 500 : 400,
                }}
              >
                {e.edge_pct == null ? "—" : `${e.edge_pct >= 0 ? "+" : ""}${e.edge_pct.toFixed(2)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function PerformanceSection() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [range, setRange] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/goal-timing/performance?range_days=${range}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!cancel) setData(res.data);
      } catch (_e) {}
    })();
    return () => { cancel = true; };
  }, [user, token, range]);

  if (!user) {
    return (
      <V3Section label="Performance tracking" testid="gt-perf-signed-out">
        <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
          Sign in to record goal-timing value bets and track ROI / CLV / calibration.
        </p>
      </V3Section>
    );
  }

  return (
    <V3Section
      label="Performance tracking"
      right={
        <V3Segment
          value={range}
          onChange={setRange}
          testidPrefix="gt-range"
          options={[
            { value: 0,  label: "Lifetime", testid: "gt-range-0"  },
            { value: 7,  label: "7d",       testid: "gt-range-7"  },
            { value: 30, label: "30d",      testid: "gt-range-30" },
            { value: 90, label: "90d",      testid: "gt-range-90" },
          ]}
        />
      }
    >
      {!data ? (
        <div className="text-[13px]" style={{ color: "var(--v3-text-4)", fontFamily: "var(--v3-font-mono)" }} data-testid="gt-perf-loading">Loading…</div>
      ) : data.summary.total_settled_bets === 0 ? (
        <div
          className="p-5 text-[13px]"
          style={{ border: "1px dashed var(--v3-line-2)", background: "var(--v3-surface)", color: "var(--v3-text-3)" }}
          data-testid="gt-perf-empty"
        >
          No settled goal-timing bets yet for this range. Record value bets from the analyse panel and settle once the match ends.
        </div>
      ) : (
        <PerfBody d={data} />
      )}
    </V3Section>
  );
}


function PerfBody({ d }) {
  const cards = [
    { l: "Total bets", v: d.summary.total_settled_bets },
    { l: "Hit rate",   v: `${d.summary.hit_rate_pct.toFixed(1)}%` },
    { l: "ROI",        v: `${d.summary.roi_pct >= 0 ? "+" : ""}${d.summary.roi_pct.toFixed(2)}%`,
      tone: d.summary.roi_pct >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" },
    { l: "P&L",        v: `${d.summary.gross_pnl >= 0 ? "+" : ""}${d.summary.gross_pnl.toFixed(2)}`,
      tone: d.summary.gross_pnl >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" },
    { l: "Avg CLV",    v: d.summary.avg_clv_pct == null ? "—"
        : `${d.summary.avg_clv_pct >= 0 ? "+" : ""}${d.summary.avg_clv_pct.toFixed(2)}%` },
  ];
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-0" style={{ borderTop: "1px solid var(--v3-line)" }}>
        {cards.map((c, i) => (
          <div
            key={c.l}
            className="p-4"
            style={{
              borderBottom: "1px solid var(--v3-line)",
              borderRight: i < cards.length - 1 ? "1px solid var(--v3-line)" : "none",
              background: "var(--v3-surface)",
            }}
          >
            <V3Label>{c.l}</V3Label>
            <div
              className="mt-2 text-xl"
              style={{
                color: c.tone || "var(--v3-text)",
                fontFamily: "var(--v3-font-mono)",
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "-0.02em",
                fontWeight: 500,
              }}
            >
              {c.v}
            </div>
          </div>
        ))}
      </div>
      <RollupTable title="By market" rows={d.by_market} />
      <RollupTable title="By league" rows={d.by_league} />
      <RollupTable title="By tier"   rows={d.by_tier}   />
    </>
  );
}


function RollupTable({ title, rows }) {
  const items = Object.entries(rows || {});
  if (!items.length) return null;
  return (
    <div className="mt-6">
      <V3Label>{title}</V3Label>
      <div className="mt-3 overflow-x-auto" style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}>
        <table className="w-full text-[13px]" style={{ fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--v3-line)" }}>
              {["Name", "Bets", "Hit rate", "ROI", "P&L"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.sort((a, b) => b[1].bets - a[1].bets).map(([n, r]) => (
              <tr key={n} style={{ borderBottom: "1px solid var(--v3-line)" }}>
                <td className="px-3 py-2" style={{ fontWeight: 500 }}>{n}</td>
                <td className="px-3 py-2">{r.bets}</td>
                <td className="px-3 py-2">{r.hit_rate_pct.toFixed(1)}%</td>
                <td className="px-3 py-2" style={{ color: r.roi_pct >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" }}>
                  {r.roi_pct >= 0 ? "+" : ""}{r.roi_pct.toFixed(2)}%
                </td>
                <td className="px-3 py-2" style={{ color: r.pnl >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" }}>
                  {r.pnl >= 0 ? "+" : ""}{r.pnl.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Small bits ────────────────────────────────────────────────────────

function Pane({ children, borderRight = false, borderBottom = false }) {
  return (
    <div
      className="p-5"
      style={{
        border: "1px solid var(--v3-line)",
        borderTop: "none",
        borderRight: borderRight ? "1px solid var(--v3-line)" : "none",
        borderBottom: borderBottom ? "1px solid var(--v3-line)" : "none",
        background: "var(--v3-surface)",
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
        {label}
      </span>
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="px-2.5 py-2 text-[13px] bg-transparent focus:outline-none"
        style={{
          border: "1px solid var(--v3-line-2)",
          color: "var(--v3-text)",
          fontFamily: "var(--v3-font-mono)",
          fontVariantNumeric: "tabular-nums",
        }}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="px-2.5 py-2 text-[13px] bg-transparent focus:outline-none"
        style={{
          border: "1px solid var(--v3-line-2)",
          color: "var(--v3-text)",
          fontFamily: "var(--v3-font-mono)",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: "var(--v3-canvas)" }}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function MonoInput({ value, onChange, placeholder, width = 100, testId, type = "text" }) {
  return (
    <input
      type={type}
      step={type === "number" ? "0.01" : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="px-2 py-1.5 text-[12px] bg-transparent focus:outline-none"
      style={{
        border: "1px solid var(--v3-line-2)",
        color: "var(--v3-text)",
        fontFamily: "var(--v3-font-mono)",
        width,
      }}
    />
  );
}

function Bar({ label, pct, tone = "var(--v3-indigo)", wide = false }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <div className={`${wide ? "w-40" : "w-16"} text-right`} style={{ color: "var(--v3-text-3)" }}>{label}</div>
      <div className="flex-1 h-1" style={{ background: "var(--v3-line)" }}>
        <div className="h-full" style={{ width: `${Math.min(100, pct)}%`, background: tone }} />
      </div>
      <div className="w-14 text-right" style={{ fontFamily: "var(--v3-font-mono)", fontVariantNumeric: "tabular-nums" }}>
        {pct.toFixed(1)}%
      </div>
    </div>
  );
}

function KVList({ rows }) {
  return (
    <div className="mt-4 text-[12px] space-y-1.5">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <span style={{ color: "var(--v3-text-3)" }}>{k}</span>
          <span>{v}</span>
        </div>
      ))}
    </div>
  );
}
